// src/api/doorsApi.js
// Doors module API (multi-tenant, separate from Windows).
// Uses your firebaseConfig.js (db export).

import {
  collection,
  doc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  getDoc,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

// ---------- Constants ----------
export const DOOR_TYPES = ['uPVC', 'Composite', 'Fire', 'Timber', 'Aluminium', 'Steel'];
// No "base" kind (per your decision)
export const KINDS = ['variant', 'door_colour', 'frame_colour', 'extra', 'accessory'];

export const DEFAULT_VISIBLE_TYPES = {
  uPVC: true,
  Composite: true,
  Fire: false,
  Timber: false,
  Aluminium: false,
  Steel: false,
};

// Finalised collections/docs (all valid segment counts)
const MASTER_ITEMS_COLL = 'doors_master_items'; // collection (root)
const ORG_ITEMS_COLL = 'doors_items'; // subcollection under each org
const ORG_SETTINGS_COLL = 'doors_settings'; // subcollection
const ORG_SETTINGS_DOCID = 'config'; // single document id

// Global per-org limits (kept as-is per your latest call)
export const ORG_LIMITS = {
  extra: { warnAt: 7, hardMax: 10 },
  accessory: { warnAt: 7, hardMax: 10 },
};

// ---------- Helpers ----------
const itemsColRefMaster = () => collection(db, MASTER_ITEMS_COLL);
const itemsColRefOrg = (orgId) => collection(db, 'organizations', orgId, ORG_ITEMS_COLL);
const settingsDocRefOrg = (orgId) =>
  doc(db, 'organizations', orgId, ORG_SETTINGS_COLL, ORG_SETTINGS_DOCID);

// Lowercase normalizer to enforce de-duplication
const toLc = (s) => (s || '').trim().toLowerCase();
const keyOf = (it) => `${(it.type || '').trim()}::${(it.kind || '').trim()}::${toLc(it.name)}`;

// Overlay logic (Admin view): prefer org override if same Type+Kind+Name exists
export function overlayAdmin(masterItems, orgItems) {
  const map = new Map();
  masterItems.forEach((m) => map.set(keyOf(m), { ...m, __source: 'master' }));
  orgItems.forEach((o) => map.set(keyOf(o), { ...o, __source: 'org' }));
  return [...map.values()];
}

// ---------- Reads ----------
export async function listMasterItems({ kind, type } = {}) {
  const snap = await getDocs(itemsColRefMaster());
  let out = [];
  snap.forEach((d) => out.push({ id: d.id, ...d.data() }));
  if (kind) out = out.filter((x) => x.kind === kind);
  if (type) out = out.filter((x) => x.type === type);
  return out.sort(bySortThenName);
}

export async function listOrgItems(orgId, { kind, type } = {}) {
  const snap = await getDocs(itemsColRefOrg(orgId));
  let out = [];
  snap.forEach((d) => out.push({ id: d.id, ...d.data() }));
  if (kind) out = out.filter((x) => x.kind === kind);
  if (type) out = out.filter((x) => x.type === type);
  return out.sort(bySortThenName);
}

function bySortThenName(a, b) {
  const sa = typeof a.sort === 'number' ? a.sort : 0;
  const sb = typeof b.sort === 'number' ? b.sort : 0;
  if (sa !== sb) return sa - sb;
  return (a.name || '').localeCompare(b.name || '');
}

/**
 * Get visible types for an org. If the doc does not exist yet,
 * we persist the defaults so sellers don't need to click "Save".
 */
export async function getVisibleTypes(orgId) {
  const ref = settingsDocRefOrg(orgId);
  const d = await getDoc(ref);

  if (!d.exists()) {
    await setDoc(
      ref,
      {
        visibleTypes: DEFAULT_VISIBLE_TYPES,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    return { ...DEFAULT_VISIBLE_TYPES };
  }

  const data = d.data() || {};
  const vt = data.visibleTypes || {};

  // If the doc exists but the key is missing, patch it with defaults
  const merged = { ...DEFAULT_VISIBLE_TYPES, ...vt };
  if (!('visibleTypes' in data)) {
    await setDoc(ref, { visibleTypes: merged, updatedAt: serverTimestamp() }, { merge: true });
  }
  return merged;
}

export async function setVisibleTypes(orgId, visibleTypes) {
  await setDoc(
    settingsDocRefOrg(orgId),
    { visibleTypes, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

// Count active, org-created Extras/Accessories for limits
export async function getOrgAddOnCounts(orgId) {
  const items = await listOrgItems(orgId);
  const isActive = (x) => x.active !== false;
  const owned = (x) => x.createdBy === 'org';
  const extras = items.filter((x) => x.kind === 'extra' && isActive(x) && owned(x)).length;
  const accessories = items.filter((x) => x.kind === 'accessory' && isActive(x) && owned(x)).length;
  return { extras, accessories };
}

// Check for duplicates (Type+Kind+Name within an Org's items ONLY)
export async function isDuplicateName(orgId, type, kind, candidateName, excludeId = null) {
  const name_lc = toLc(candidateName);
  const oAll = await listOrgItems(orgId, { type, kind });

  // An org cannot have two items with the same name, type, and kind.
  // We exclude the item being edited from this check.
  const orgExists = oAll.some((x) => {
    return toLc(x.name) === name_lc && x.id !== excludeId;
  });

  return orgExists;
}


// ---------- Writes ----------
export async function createOrgItem(orgId, payload) {
  // payload: { kind, type, name, unit, price, active, sort }
  const now = serverTimestamp();
  const data = {
    ...payload,
    name_lc: toLc(payload.name),
    createdBy: 'org',
    createdAt: now,
    updatedAt: now,
  };
  const ref = await addDoc(itemsColRefOrg(orgId), data);
  return { id: ref.id, ...data };
}

export async function updateOrgItem(orgId, id, patch) {
  const ref = doc(db, 'organizations', orgId, ORG_ITEMS_COLL, id);
  const data = {
    ...patch,
    ...(patch.name ? { name_lc: toLc(patch.name) } : {}),
    updatedAt: serverTimestamp(),
  };
  await updateDoc(ref, data);
}

export async function archiveOrgItem(orgId, id, active) {
  const ref = doc(db, 'organizations', orgId, ORG_ITEMS_COLL, id);
  await updateDoc(ref, { active, updatedAt: serverTimestamp() });
}

// When admin "edits" a master item, we create/update an org override with the same Type+Kind+Name
export async function ensureOrgOverrideForMaster(orgId, masterItem, patch) {
  const oItems = await listOrgItems(orgId, { type: masterItem.type, kind: masterItem.kind });
  const existing = oItems.find((x) => toLc(x.name) === toLc(masterItem.name));
  const payload = {
    kind: masterItem.kind,
    type: masterItem.type,
    name: masterItem.name,
    unit: masterItem.unit,
    price: masterItem.price,
    active: masterItem.active !== false,
    sort: typeof masterItem.sort === 'number' ? masterItem.sort : 0,
    ...patch,
  };
  if (existing) {
    await updateOrgItem(orgId, existing.id, payload);
    return { ...existing, ...payload };
  }
  return await createOrgItem(orgId, payload);
}

// ---------- Starter Pack removed by design ----------
export async function addStarterPackToOrg() {
  return { ok: false, message: 'Starter pack is disabled. Use the catalogue to add/edit items.' };
}